import { DataSelectionBinding } from '../../../models/data-selection';
import { DataListComponent, DataListSortOption } from '../../../shared/ui/data-list/data-list.component';
import { DataQuery, DataPage } from '../../../models/data-source';
import { Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, finalize, map, takeUntil, tap, toArray } from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';
import { CustomerApiService, CustomerDto, CustomerProfileDto } from '../../../services/customer-api.service';
import {
  CustomerAssigneeLookup, CustomerCatalogLookup, CustomerTypeLookup, LookupApiService, StateLookup,
} from '../../../services/lookup-api.service';
import { DataTableColumn, DataTableComponent } from '../../../shared/ui/data-table/data-table.component';
import {
  DataFilterField, DataFilterOption, DataFilterRule, DataFilterValue, DataFilterValues, isRule,
} from '../../../shared/ui/data-filter/data-filter.component';
import { toRuleParams } from '../../../shared/ui/data-filter/data-filter.rules';
import {
  CustomerFeature, CustomerMapFilter,
} from '../../../map/features/customers/customer-feature';
import { AddChildDialogComponent } from './add-child-dialog.component';
import { ExportScope } from '../../../components/table-export-dialog/table-export-dialog.component';
import { CsvColumn } from '../../../shared/csv-export';
import { TableExport } from '../../../shared/table-export';
import { deleteAllData } from '../../../shared/delete-all-data';
import { todayLocalDateString } from '../../../shared/date.util';

@Component({
  selector: 'app-customers',
  templateUrl: './customers.component.html',
  styleUrls: ['./customers.component.css'],
})
export class CustomersComponent implements OnChanges, OnInit, OnDestroy {
  /** The map supplies business IDs; Sidebar leaves the collection unscoped. */
  @Input() selectedIds: readonly number[] | null = null;
  @Input() selectionBinding: DataSelectionBinding | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedIds']) this.filterValues = { ...this.filterValues };
  }
  readonly trackCustomer = (_index: number, row: CustomerDto): number => row.id;

  @ViewChild('compactList') compactList?: DataListComponent<CustomerDto>;

  readonly compactSortOptions: readonly DataListSortOption[] = [
    { label: 'Name A–Z', field: 'displayName' },
    { label: 'Name Z–A', field: 'displayName', desc: true },
    { label: 'Next follow-up', field: 'nextFollowUp' },
    { label: 'Latest activity', field: 'lastActivity', desc: true },
    { label: 'Recently updated', field: 'updatedAt', desc: true },
  ];

  // Every customer data column is sortable; the shared table uses each id as
  // the server sort key.
  readonly columns: DataTableColumn[] = [
    { id: 'id', label: 'Acct #' },
    { id: 'displayName', label: 'Name' },
    { id: 'assignee', label: 'Assignee' },
    { id: 'customerTypeName', label: 'Type' },
    { id: 'nextFollowUp', label: 'Next follow-up' },
    { id: 'lastActivity', label: 'Last activity' },
    { id: 'createdAt', label: 'Created', align: 'end' },
    { id: 'updatedAt', label: 'Updated', align: 'end' },
    // Every other business field on the customer, in the order a record is
    // read: how to reach them, where they are, how they are billed.
    { id: 'accountNumber', label: 'Account number' },
    { id: 'contact', label: 'Contact' },
    { id: 'phone', label: 'Phone' },
    { id: 'ext', label: 'Ext' },
    { id: 'mobilePhone', label: 'Mobile' },
    { id: 'email', label: 'Email' },
    { id: 'fax', label: 'Fax' },
    { id: 'website', label: 'Website' },
    { id: 'serviceAddress', label: 'Address' },
    { id: 'serviceCity', label: 'City' },
    { id: 'serviceState', label: 'State' },
    { id: 'servicePostalCode', label: 'ZIP' },
    { id: 'billingName', label: 'Billing name' },
    { id: 'billingCompanyName', label: 'Billing company' },
    { id: 'billingAddress', label: 'Billing address' },
    { id: 'billingCity', label: 'Billing city' },
    { id: 'billingState', label: 'Billing state' },
    { id: 'billingPostalCode', label: 'Billing ZIP' },
    { id: 'billingStatus', label: 'Billing status' },
    { id: 'paymentTerms', label: 'Payment terms' },
    { id: 'taxArea', label: 'Tax area' },
    { id: 'pricingArea', label: 'Pricing area' },
    { id: 'isTaxExempt', label: 'Tax exempt' },
    { id: 'taxExemptCertificateRef', label: 'Tax exempt cert #' },
    { id: 'commPref', label: 'Communication preference' },
    { id: 'source', label: 'Source' },
    { id: 'notes', label: 'Notes' },
  ];

  /**
   * What the table opens on: the eight columns it has always shown. Every
   * other business field is one tick away in the column picker, which
   * remembers the choice per user - all of them at once would open the table
   * several frames wide beside the map.
   */
  readonly defaultHiddenColumns: readonly string[] = [
    'accountNumber', 'contact', 'phone', 'ext', 'mobilePhone', 'email', 'fax',
    'website', 'serviceAddress', 'serviceCity', 'serviceState',
    'servicePostalCode', 'billingName', 'billingCompanyName', 'billingAddress',
    'billingCity', 'billingState', 'billingPostalCode', 'billingStatus',
    'paymentTerms', 'taxArea', 'pricingArea', 'isTaxExempt',
    'taxExemptCertificateRef', 'commPref', 'source', 'notes',
  ];
  readonly listFields = [
    'id', 'displayName', 'address', 'lat', 'lng', 'assignee', 'customerTypeName', 'nextFollowUp', 'lastActivity',
  ];

  filterFields: DataFilterField[] = [];
  filterValues: DataFilterValues = { customerTypeId: null };

  /**
   * The rule-based half of the bar, kept so the query builder and the bar
   * agree on which values are rules. Everything else in filterValues belongs
   * to a filter with a query parameter of its own.
   */
  private ruleFields: DataFilterField[] = [];
  private customerTypes: CustomerTypeLookup[] = [];
  private assignees: CustomerAssigneeLookup[] = [];
  private states: StateLookup[] = [];
  private sources: string[] = [];
  private billingStatuses: CustomerCatalogLookup[] = [];
  private pricingAreas: CustomerCatalogLookup[] = [];
  private communicationPreferences: CustomerCatalogLookup[] = [];

  /** What the map layer was last told, so an unchanged bar does not refetch it. */
  private mapFilterKey: string | null = null;

  private readonly destroyed$ = new Subject<void>();
  @ViewChild(DataTableComponent) table?: DataTableComponent<CustomerDto>;

  constructor(
    private api: CustomerApiService,
    private lookups: LookupApiService,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    public auth: AuthService,
    private readonly customerLayer: CustomerFeature,
  ) {}

  ngOnInit(): void {
    this.buildFilterFields();
    this.syncMapFilter();
    // A customer clicked inside a map popover comes back here. The layer
    // filters to its own objects, so this page hears only about customers.
    this.customerLayer.revealRequests$.pipe(takeUntil(this.destroyed$))
      .subscribe(objectId => this.revealFromMap(objectId));
    this.keepLookup(this.lookups.customerTypes(), items => { this.customerTypes = items; });
    // The roster behind the Assignee rule.
    this.keepLookup(this.lookups.customerAssignees(), items => { this.assignees = items; });
    this.keepLookup(this.lookups.states(), items => { this.states = items; });
    this.keepLookup(this.lookups.customerSources(), items => { this.sources = items; });
    this.keepLookup(this.lookups.billingStatuses(), items => { this.billingStatuses = items; });
    this.keepLookup(this.lookups.pricingAreas(), items => { this.pricingAreas = items; });
    this.keepLookup(
      this.lookups.communicationPreferences(),
      items => { this.communicationPreferences = items; });
  }

  /**
   * Keeps one list behind a dropdown and redraws the bar when it arrives. A
   * failed read leaves that dropdown empty, which offers nothing rather than
   * offering nonsense.
   */
  private keepLookup<T>(source: Observable<T[]>, keep: (items: T[]) => void): void {
    source.pipe(
      catchError(() => of([] as T[])),
      takeUntil(this.destroyed$),
    ).subscribe(items => {
      keep(items);
      this.buildFilterFields();
    });
  }

  /**
   * Rebuilt when a lookup arrives rather than computed in a getter: the
   * template reads this on every change-detection pass, and a fresh array each
   * time would re-enter the filter bar's ngOnChanges for no reason.
   */
  private buildFilterFields(): void {
    this.ruleFields = this.buildRuleFilterFields();
    this.filterFields = [
      // Pinned, and not a rule: customer type has always had its own query
      // parameter, which the children endpoint and the map share. Restating it
      // as a rule would put two controls for one column on the same bar.
      {
        id: 'customerTypeId',
        label: 'Customer type',
        allLabel: 'All customer types',
        options: this.customerTypes.map(type => ({
          value: type.id,
          label: type.name,
        })),
      },
      ...this.ruleFields,
    ];
  }

  /**
   * The columns this table can be narrowed by, in the grammar the Leads board
   * already speaks - one repeated `rule` parameter, each `field:op:value`. The
   * server holds the whitelist of columns and comparisons and answers 400 on
   * anything else, so a rule invented here is a visible failure rather than a
   * filter that quietly does nothing.
   *
   * Unpinned, so the bar stays as short as it was until someone adds a column
   * from the "+" menu.
   *
   * The ids are the table's own column ids, which is what lets the header and
   * the filter agree about which column is being talked about - and every
   * column the picker can show is here, customer type aside.
   */
  private buildRuleFilterFields(): DataFilterField[] {
    const none: readonly DataFilterOption[] = [];
    const catalogue = (items: readonly CustomerCatalogLookup[]): DataFilterOption[] =>
      items.map(item => ({ value: item.id, label: item.name }));
    // The value is the state id the address holds; the label is its code, as
    // the State columns show it.
    const states = this.states.map(state => ({ value: state.id, label: state.code }));
    return [
      { id: 'id', label: 'Acct #', kind: 'number', pinned: false, options: none },
      { id: 'displayName', label: 'Name', kind: 'text', pinned: false, options: none },
      {
        id: 'assignee', label: 'Assignee', pinned: false, allLabel: 'Any assignee',
        // Value is the user id, which is what the API filters on; the label is
        // the same person written for somebody to read.
        options: this.assignees.map(user => ({
          value: user.id,
          label: user.displayName ?? user.userName ?? 'User ' + user.id,
        })),
      },
      {
        id: 'nextFollowUp', label: 'Next follow-up', kind: 'date', pinned: false,
        options: none,
      },
      {
        id: 'lastActivity', label: 'Last activity', kind: 'date', pinned: false,
        options: none,
      },
      { id: 'createdAt', label: 'Created', kind: 'date', pinned: false, options: none },
      { id: 'updatedAt', label: 'Updated', kind: 'date', pinned: false, options: none },
      {
        id: 'accountNumber', label: 'Account number', kind: 'text', pinned: false,
        options: none,
      },
      { id: 'contact', label: 'Contact', kind: 'text', pinned: false, options: none },
      { id: 'phone', label: 'Phone', kind: 'text', pinned: false, options: none },
      { id: 'ext', label: 'Ext', kind: 'text', pinned: false, options: none },
      { id: 'mobilePhone', label: 'Mobile', kind: 'text', pinned: false, options: none },
      { id: 'email', label: 'Email', kind: 'text', pinned: false, options: none },
      { id: 'fax', label: 'Fax', kind: 'text', pinned: false, options: none },
      { id: 'website', label: 'Website', kind: 'text', pinned: false, options: none },
      { id: 'serviceAddress', label: 'Address', kind: 'text', pinned: false, options: none },
      { id: 'serviceCity', label: 'City', kind: 'text', pinned: false, options: none },
      {
        id: 'serviceState', label: 'State', pinned: false, allLabel: 'Any state',
        options: states,
      },
      { id: 'servicePostalCode', label: 'ZIP', kind: 'text', pinned: false, options: none },
      { id: 'billingName', label: 'Billing name', kind: 'text', pinned: false, options: none },
      {
        id: 'billingCompanyName', label: 'Billing company', kind: 'text', pinned: false,
        options: none,
      },
      {
        id: 'billingAddress', label: 'Billing address', kind: 'text', pinned: false,
        options: none,
      },
      { id: 'billingCity', label: 'Billing city', kind: 'text', pinned: false, options: none },
      {
        id: 'billingState', label: 'Billing state', pinned: false, allLabel: 'Any state',
        options: states,
      },
      {
        id: 'billingPostalCode', label: 'Billing ZIP', kind: 'text', pinned: false,
        options: none,
      },
      {
        id: 'billingStatus', label: 'Billing status', pinned: false,
        allLabel: 'Any billing status', options: catalogue(this.billingStatuses),
      },
      // Named rather than picked: neither catalogue has a screen anywhere in
      // the app that fills it, so a dropdown would be an empty list.
      { id: 'paymentTerms', label: 'Payment terms', kind: 'text', pinned: false, options: none },
      { id: 'taxArea', label: 'Tax area', kind: 'text', pinned: false, options: none },
      {
        id: 'pricingArea', label: 'Pricing area', pinned: false,
        allLabel: 'Any pricing area', options: catalogue(this.pricingAreas),
      },
      {
        id: 'isTaxExempt', label: 'Tax exempt', pinned: false, allLabel: 'Either',
        options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }],
      },
      {
        id: 'taxExemptCertificateRef', label: 'Tax exempt cert #', kind: 'text',
        pinned: false, options: none,
      },
      {
        id: 'commPref', label: 'Communication preference', pinned: false,
        allLabel: 'Any preference', options: catalogue(this.communicationPreferences),
      },
      {
        id: 'source', label: 'Source', pinned: false, allLabel: 'Any source',
        options: this.sources.map(source => ({ value: source, label: source })),
      },
      { id: 'notes', label: 'Notes', kind: 'text', pinned: false, options: none },
    ];
  }

  ngOnDestroy(): void {
    // The layer outlives this page. Leaving it narrowed would hide customers
    // from every other screen that draws them.
    this.customerLayer.filter({});
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // Server-mode source for the table. The page maps shared query filters to
  // the endpoint-specific customer request.
  fetch = (q: DataQuery): Observable<DataPage<CustomerDto>> =>
    this.list(q).pipe(tap(page => {
      // What Export can reach without asking the server again.
      //
      // Captured from the TABLE's fetch rather than inside `list`, because the
      // compact list reads the same endpoint with its own fields and its own
      // paging. Were this a step of `list`, opening the compact view would
      // quietly redefine what "this page" and "everything matching" mean, and
      // Export would write that instead of what the table is showing.
      this.pageRows = page.items;
      this.listTotal = page.total;
      this.listSort = q.sort ? { field: q.sort, desc: q.desc ?? false } : null;
    }));

  /** Compact summaries request their fields independently of visible table columns. */
  fetchCompact = (query: DataQuery) => this.list({
    ...query,
    fields: ['id', 'displayName', 'address', 'lat', 'lng', 'contact', 'primaryContact', 'phone', 'ext', 'mobilePhone'],
  });

  /** The list request itself, with no view's state attached to it. */
  private list(q: DataQuery): Observable<DataPage<CustomerDto>> {
    return this.api.listCustomers({
      selectedIds: this.selectedIds,
      sort: q.sort || undefined,
      desc: q.desc,
      page: q.page,
      pageSize: q.pageSize,
      customerTypeId: this.customerTypeId(q.filters),
      rules: this.ruleParams(q.filters),
      fields: this.customerFields(q.fields ?? this.listFields),
    });
  }

  /** Children are not part of the root page count; one parent loads them on demand. */
  fetchChildren = (parent: CustomerDto, q: DataQuery): Observable<CustomerDto[]> =>
    this.api.getCustomerChildren(parent.id, {
      customerTypeId: this.customerTypeId(q.filters),
      rules: this.ruleParams(q.filters),
      sort: q.sort || undefined,
      desc: q.desc,
      fields: this.customerFields(q.fields ?? this.listFields),
    });

  /**
   * The DTO fields behind a visible column, where they are not the column
   * itself: an assignee is an id and a name, and a catalogue column shows the
   * name while its filter picks by id.
   */
  private static readonly FIELDS_FOR_COLUMN: Readonly<Record<string, readonly string[]>> = {
    assignee: ['assigneeUserId', 'assigneeName'],
    lastActivity: ['lastActivityAt'],
    billingStatus: ['billingStatusName'],
    paymentTerms: ['paymentTermsName'],
    taxArea: ['taxAreaName'],
    pricingArea: ['pricingAreaName'],
    commPref: ['commPrefName'],
  };

  private customerFields(columns: readonly string[]): string[] {
    return columns.flatMap(column => CustomersComponent.FIELDS_FOR_COLUMN[column] ?? [column]);
  }

  // -- What the map asks for ---------------------------------------------
  //
  // The mirror of a row asking the map to show a customer. The list finds the
  // row and marks it; if it cannot, the rules the map was never told about
  // are the only things that can hide a customer the map is drawing, so they
  // come off and both halves of the screen show the same customers.

  /** The customer asked for, and whether its filters have been widened. */
  private pendingReveal: { id: number; widened: boolean } | null = null;

  /** The line under the filter bar, and what Undo would put back. */
  revealNotice: string | null = null;
  private revealRestore: DataFilterValues | null = null;

  get canRestoreRevealFilters(): boolean { return !!this.revealRestore; }

  private revealFromMap(objectId: number | string): void {
    const id = Number(objectId);
    if (!Number.isFinite(id)) return;
    this.pendingReveal = { id, widened: false };
    this.revealNotice = null;
    this.compactList?.reveal(row => row.id === id);
  }

  /** The list read every page it had and still did not find it. */
  onCompactRevealMissed(): void {
    const pending = this.pendingReveal;
    if (!pending) return;
    const dropped = pending.widened ? [] : this.dropRulesTheMapDoesNotShare();
    if (!dropped.length) {
      this.pendingReveal = null;
      this.revealNotice = 'That customer is not in this list.';
      return;
    }
    pending.widened = true;
    this.revealNotice =
      `Cleared ${dropped.join(' and ')} so this list shows what the map shows.`;
    // Dropping the rules re-reads the rows; the list is still looking for the
    // same customer, so it finds it as the new pages land.
    this.compactList?.reveal(row => row.id === pending.id);
  }

  /** Puts back what was cleared to show that customer. */
  restoreRevealedFilters(): void {
    const restore = this.revealRestore;
    if (!restore) return;
    this.revealRestore = null;
    this.revealNotice = null;
    this.pendingReveal = null;
    this.applyFilters(restore);
  }

  /**
   * What syncMapFilter passes on, and therefore what may stay: the type, the
   * assignee, and the one text rule that crossed over as the map's search.
   * The rest - a city, a billing status - has no equivalent on the map, which
   * is why the map can be drawing a customer this list is hiding.
   */
  private rulesTheMapShares(): string[] {
    const shared = ['customerTypeId', 'assignee'];
    for (const field of CustomersComponent.MapSearchRules) {
      if (wideningText(this.filterValues[field]) !== null) {
        shared.push(field);
        break;
      }
    }
    return shared;
  }

  /** Returns what it cleared, named the way the filter bar names it. */
  private dropRulesTheMapDoesNotShare(): string[] {
    const shared = this.rulesTheMapShares();
    const values = this.filterValues;
    const dropped = Object.keys(values).filter(id =>
      !shared.includes(id) && hasRuleValue(values[id]));
    if (!dropped.length) return [];

    this.revealRestore = { ...values };
    const kept: Record<string, DataFilterValue | DataFilterRule> = {};
    for (const id of Object.keys(values)) {
      if (!dropped.includes(id)) kept[id] = values[id];
    }
    this.applyFilters(kept);
    return dropped.map(id =>
      this.filterFields.find(field => field.id === id)?.label ?? id);
  }

  applyFilters(values: DataFilterValues): void {
    this.filterValues = values;
    this.syncMapFilter();
  }

  private customerTypeId(filters: DataFilterValues): number | null {
    const value = filters['customerTypeId'];
    return typeof value === 'number' ? value : null;
  }

  /**
   * The Customers layer on the map beside this page is narrowed the way this
   * list is.
   *
   * The two halves of the screen used to answer different questions: the list
   * showed the customers somebody asked for, and the map beside it showed all
   * of them.
   *
   * What crosses over is what both sides say the same way. Customer type and
   * Assignee are columns the map's own filter has always carried, so they go
   * as themselves. A rule on a column the map's search reads - the name, the
   * account number, the email, the phone (CustomerLogic.ApplySearch) - goes as
   * that search, which matches MORE rows than the rule does: the map may draw
   * a pin this list filtered out, and can never hide one it is showing.
   *
   * A rule on any other column - a city, a billing status - has no equivalent
   * on the map endpoint. Guessing at one would mean a map that quietly drops
   * customers the list is showing, so it is left out and the map stays wider
   * than the list rather than wrong.
   */
  private syncMapFilter(): void {
    const filter = this.mapFilter();
    const key = JSON.stringify(filter);
    // Every tile is refetched behind this call, so an unchanged filter - the
    // bar re-applied, a column sorted - must not reach the layer.
    if (key === this.mapFilterKey) return;
    this.mapFilterKey = key;
    this.customerLayer.filter(filter);
  }

  private mapFilter(): CustomerMapFilter {
    const customerTypeId = this.customerTypeId(this.filterValues);
    const assigneeUserId = ruleId(this.filterValues['assignee']);
    const search = this.mapSearch();
    return {
      ...(customerTypeId !== null ? { customerTypeId } : {}),
      ...(assigneeUserId !== null ? { assigneeUserId } : {}),
      ...(search !== null ? { search } : {}),
    };
  }

  /** The columns the map's own search reads, in the order this page prefers. */
  private static readonly MapSearchRules = ['displayName', 'accountNumber', 'email', 'phone'];

  /**
   * One text rule, as the map's search.
   *
   * The first one found is enough even when several are set: the list ANDs
   * them, so any single one of them describes a wider set than the list shows
   * - which is the direction that keeps every listed customer on the map.
   */
  private mapSearch(): string | null {
    for (const field of CustomersComponent.MapSearchRules) {
      const text = wideningText(this.filterValues[field]);
      if (text !== null) return text;
    }
    return null;
  }

  /**
   * The rule half of the bar, as `field:op:value` strings.
   *
   * Read from the declared rule fields rather than from every key in the value
   * object, so the pinned customer-type filter cannot be sent twice - once as
   * its own parameter and once as a rule the server would refuse.
   */
  private ruleParams(filters: DataFilterValues): string[] {
    return toRuleParams(filters, this.ruleFields);
  }

  onDelete(row: CustomerDto): void {
    if (!confirm(`Deactivate "${row.displayName}"? It will be hidden from the active list.`)) return;
    this.api.deactivateCustomer(row.id).pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.snackBar.open('Customer deactivated', undefined, { duration: 2500 });
        this.table?.refresh([row]);
        this.compactList?.reload();
      },
      error: e => this.snackBar.open(e?.error?.error ?? 'Could not deactivate customer.', 'OK', { duration: 5000 }),
    });
  }

  // Bulk-deactivate the selected rows. Per-row failures (e.g. a master with active
  // children) are counted, not fatal, so the rest still go through.
  deactivateSelected(): void {
    const rows = this.table?.selection.selected ?? [];
    if (!rows.length) return;
    if (!confirm(`Deactivate ${rows.length} customer(s)? They will be hidden from the active list.`)) return;
    // Children go first and requests are serialized: when a selected parent and
    // all of its selected children are deactivated together, the parent's
    // active-child guard sees the final child state instead of racing it.
    const ordered = [...rows].sort((a, b) =>
      Number(this.table?.isChildRow(b)) - Number(this.table?.isChildRow(a)));
    from(ordered).pipe(
      concatMap(r => this.api.deactivateCustomer(r.id)
        .pipe(map(() => true), catchError(() => of(false)))),
      toArray(),
      takeUntil(this.destroyed$),
    ).subscribe(results => {
      const failed = results.filter(ok => !ok).length;
      this.snackBar.open(
        failed ? `Deactivated ${results.length - failed}; ${failed} could not be (e.g. a master with active children).`
               : `Deactivated ${results.length} customer(s).`,
        failed ? 'OK' : undefined, { duration: failed ? 6000 : 2500 });
      this.table?.clearSelection();
      this.table?.refresh(ordered);
      this.compactList?.reload();
    });
  }

  goToCustomer(id: number): void { this.router.navigate(['/customers/table', id]); }
  /** An import or a merge changed the rows, so the table reads them again.
   *  Which dialog opened, told which table, is the shared transfer actions'. */
  onTransferChanged(): void {
    this.table?.reload();
    this.compactList?.reload();
  }

  addCustomer(): void {
    this.router.navigate(['/customers/new']);
  }

  updateCompactCustomer(row: CustomerDto, profile: CustomerProfileDto): void {
    row.displayName = profile.customer.displayName;
    row.contact = profile.customer.contact;
    if (profile.customer.primaryContact !== undefined) row.primaryContact = profile.customer.primaryContact;
    row.phone = profile.customer.phone;
    row.ext = profile.customer.ext;
    row.mobilePhone = profile.customer.mobilePhone;
    const address = profile.serviceAddress;
    row.address = profile.customer.address ?? ([address?.streetAddressLine1,
      address?.streetAddressLine2, address?.city, address?.postalCode].filter(Boolean).join(' ') || row.address);
  }

  editCustomer(row: CustomerDto): void {
    this.router.navigate(['/customers/table', row.id, 'edit']);
  }

  canAddChild(row: CustomerDto): boolean {
    return !this.table?.isChildRow(row);
  }

  openAddChildDialog(parent: CustomerDto): void {
    this.dialog.open(AddChildDialogComponent, {
      data: { masterId: parent.id, masterName: parent.displayName },
      maxWidth: '96vw',
      disableClose: true,
    }).afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(result => {
      if (!result) return;
      this.table?.refresh([parent]);
      this.compactList?.reload();
    });
  }

  // ── Export ──────────────────────────────────────────────────────────────
  //
  // The flow the Leads table runs, on the shared runner: ask which rows, then
  // write the file here from the table's own columns.
  //
  // It replaced a server endpoint that always returned the whole table under a
  // column set of its own. What that cost is that the file now follows this
  // page's columns rather than the server's; what it buys is that a filtered
  // view or a set of ticked rows exports what is actually on screen, which is
  // what pressing Export in front of a filtered table means.

  /** What the table is showing, kept for Export to write without re-asking. */
  private pageRows: CustomerDto[] = [];
  private listTotal = 0;
  private listSelection: CustomerDto[] = [];
  private listSort: { field: string; desc: boolean } | null = null;

  /**
   * The server's own maximum for this endpoint, and not a row more.
   *
   * The list clamps anything above 100 back to 25 rather than down to 100
   * (CustomerLogic.ListMasterAsync). Asking for more would hand back short
   * pages while the walk - which works out how many pages to read by dividing
   * the total by this - believed it had read whole ones, and the file would be
   * quietly missing most of the table.
   */
  private readonly exportPageSize = 100;

  get exporting(): boolean { return this.exporter.busy; }
  get exportError(): string | null { return this.exporter.error; }

  /** How many rows the table is actually showing right now. */
  get listPageCount(): number { return this.pageRows.length; }

  onSelectionChange(rows: CustomerDto[]): void {
    this.listSelection = rows;
  }

  private exportRunner?: TableExport<CustomerDto>;

  /**
   * What Export can reach here, and what a file of it looks like. How it asks,
   * gathers and writes is the shared runner's.
   *
   * Built on first use rather than as a field initializer, because everything
   * it closes over arrives with the constructor.
   */
  private get exporter(): TableExport<CustomerDto> {
    return this.exportRunner ??= new TableExport<CustomerDto>({
      title: 'customers',
      filename: () => `customers-${todayLocalDateString()}.csv`,
      columns: () => this.exportColumns(),
      selection: () => this.listSelection,
      pageRows: () => this.pageRows,
      filteredTotal: () => this.listTotal,
      unfilteredTotal: () => this.unfilteredTotal(),
      pageSize: this.exportPageSize,
      fetchPage: (page, filtered) => this.fetchExportPage(page, filtered),
    }, this.dialog);
  }

  /** Ask which rows, then write them. */
  openExport(): void {
    this.exporter.open();
  }

  /** Writes the file for a scope the caller has already settled. Called with
   *  nothing it writes the ticked rows, or the page when none are ticked. */
  exportCsv(scope?: ExportScope): void {
    this.exporter.run(scope);
  }

  /** Rows in the table with no filter on it, or null if the server would not
   *  say. Never errors: the caller opens a dialog either way. */
  private unfilteredTotal(): Observable<number | null> {
    return this.api.listCustomers({ selectedIds: this.selectedIds, page: 1, pageSize: 1, fields: ['id'] }).pipe(
      map(page => page.total as number | null),
      catchError(() => of(null)),
    );
  }

  /** A page for the export only: the same filter and order as the table, but
   *  it must not disturb the table's own rows, selection or total. */
  private fetchExportPage(page: number, filtered: boolean): Observable<CustomerDto[]> {
    return this.api.listCustomers({
      selectedIds: this.selectedIds,
      page,
      pageSize: this.exportPageSize,
      // Sort is kept either way - it is how the file is ordered, not what is
      // in it. The filter is what "all the table" means to leave off.
      sort: this.listSort?.field,
      desc: this.listSort?.desc,
      customerTypeId: filtered ? this.customerTypeId(this.filterValues) : undefined,
      rules: filtered ? this.ruleParams(this.filterValues) : undefined,
      // Every column the table CAN show, plus the coordinates it has no column
      // for - not the columns it happens to be showing. Which columns are
      // hidden is a reading preference; the file is the record.
      fields: this.customerFields(
        [...this.columns.map(column => column.id), 'lat', 'lng']),
    }).pipe(map(result => result.items));
  }

  /**
   * What each table column writes into the file.
   *
   * Keyed by column id, so the file is built FROM `columns` rather than from a
   * second list beside it - which is how an export comes to be missing the
   * column somebody added to the table last month.
   */
  private readonly exportCells:
      Record<string, (row: CustomerDto) => string | number | null | undefined> = {
    id: row => row.id,
    displayName: row => row.displayName,
    assignee: row => row.assigneeName,
    customerTypeName: row => row.customerTypeName,
    nextFollowUp: row => row.nextFollowUp,
    lastActivity: row => row.lastActivityAt,
    createdAt: row => row.createdAt,
    updatedAt: row => row.updatedAt,
    accountNumber: row => row.accountNumber,
    contact: row => row.contact,
    phone: row => row.phone,
    ext: row => row.ext,
    mobilePhone: row => row.mobilePhone,
    email: row => row.email,
    fax: row => row.fax,
    website: row => row.website,
    serviceAddress: row => row.serviceAddress,
    serviceCity: row => row.serviceCity,
    serviceState: row => row.serviceState,
    servicePostalCode: row => row.servicePostalCode,
    billingName: row => row.billingName,
    billingCompanyName: row => row.billingCompanyName,
    billingAddress: row => row.billingAddress,
    billingCity: row => row.billingCity,
    billingState: row => row.billingState,
    billingPostalCode: row => row.billingPostalCode,
    billingStatus: row => row.billingStatusName,
    paymentTerms: row => row.paymentTermsName,
    taxArea: row => row.taxAreaName,
    pricingArea: row => row.pricingAreaName,
    // What the cell shows, not the raw boolean - and blank rather than "No"
    // where the field was never read, so a column of blanks is not mistaken
    // for a table in which nobody is exempt.
    isTaxExempt: row =>
      row.isTaxExempt == null ? '' : (row.isTaxExempt ? 'Yes' : 'No'),
    taxExemptCertificateRef: row => row.taxExemptCertificateRef,
    commPref: row => row.commPrefName,
    source: row => row.source,
    notes: row => row.notes,
  };

  /**
   * The header a column is written under, where the importer knows it by
   * another name.
   *
   * A file this page exports should be a file the Import dialog can read, and
   * the importer talks in the transfer schema's field names rather than in the
   * labels above the columns - it has never heard of "Acct #" or "ZIP".
   * Columns absent from this map are ones the import does not read back
   * anyway, and keep the label the table gives them.
   */
  private readonly exportHeaders: Record<string, string> = {
    displayName: 'DisplayName',
    accountNumber: 'AccountNumber',
    serviceAddress: 'ServiceAddressLine1',
    serviceCity: 'ServiceCity',
    serviceState: 'ServiceState',
    servicePostalCode: 'ServiceZip',
    phone: 'Phone',
    mobilePhone: 'MobilePhone',
    email: 'Email',
    contact: 'Contact',
    website: 'Website',
    fax: 'Fax',
    billingAddress: 'BillingAddressLine1',
    billingCity: 'BillingCity',
    billingState: 'BillingState',
    billingPostalCode: 'BillingZip',
  };

  exportColumns(): CsvColumn<CustomerDto>[] {
    return [
      ...this.columns.map(column => ({
        header: this.exportHeaders[column.id] ?? column.label,
        cell: this.exportCells[column.id],
      })),
      // The service coordinates, which the table has no column for but the
      // transfer schema carries - at the end, where an added column lands in a
      // spreadsheet. Exported and never read back: the importer answers an
      // edit to one with an IgnoredEdit, and says so in its own schema.
      { header: 'ServiceLat', cell: row => row.lat },
      { header: 'ServiceLng', cell: row => row.lng },
    ];
  }

  deletingAll = false;

  deleteAllCustomers(): void {
    if (this.deletingAll) return;
    this.deletingAll = true;
    deleteAllData(this.dialog, this.snackBar, {
      noun: 'customer',
      run: () => this.api.deleteAllCustomers(),
      reload: () => {
        this.table?.clearSelection();
        this.table?.refresh();
        this.compactList?.reload();
      },
    }).pipe(
      finalize(() => { this.deletingAll = false; }),
      takeUntil(this.destroyed$),
    ).subscribe();
  }
}

/**
 * A choice rule's id, where it is an "is" - the only comparison the map's own
 * type and assignee parameters can express.
 */
function ruleId(held: DataFilterValue | DataFilterRule | undefined): number | null {
  if (held === null || held === undefined || held === '') return null;
  if (isRule(held) && held.op !== 'eq') return null;
  const value = Number(isRule(held) ? held.value : held);
  return Number.isFinite(value) ? value : null;
}

/**
 * A text rule's needle, where the map's search asks a WIDER question than the
 * rule does - "contains" and "is". Any other comparison would narrow the map
 * past the list and hide a customer the list is showing.
 */
/**
 * Whether a rule on the bar is actually narrowing anything.
 *
 * An empty rule sits on the bar as a half-written thought, so clearing it
 * would take something away and give nothing back.
 */
function hasRuleValue(
  value: DataFilterValue | DataFilterRule | undefined,
): boolean {
  if (value === null || value === undefined || value === '') return false;
  return !isRule(value) || value.value !== '';
}

function wideningText(held: DataFilterValue | DataFilterRule | undefined): string | null {
  if (held === null || held === undefined || held === '') return null;
  const op = isRule(held) ? held.op : 'eq';
  if (op !== 'contains' && op !== 'eq') return null;
  const value = String(isRule(held) ? held.value : held).trim();
  return value === '' ? null : value;
}
